import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  Tooltip,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Visibility,
  Person,
  Email,
  Phone
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import httpClient from '@utils/httpClient';

interface JobApplication {
  id: number;
  job_id: number;
  job_title: string;
  applicant_name: string;
  applicant_email: string;
  applicant_phone?: string;
  resume_filename?: string;
  resume_path?: string;
  cover_letter?: string;
  status: 'pending' | 'reviewed' | 'shortlisted' | 'rejected' | 'hired';
  applied_at: string;
}

const statusColors: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  reviewed: 'info',
  shortlisted: 'warning',
  rejected: 'error',
  hired: 'success'
};

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'hired', label: 'Hired' }
];

const JobApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterJobId, setFilterJobId] = useState<string>(searchParams.get('job') || '');
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterJobId) params.append('job_id', filterJobId);

      const response = await httpClient.get(`/dashboard-analytics/applications?${params}`);
      if (response.success) {
        setApplications(response.applications || []);
      } else {
        setError(response.error?.message || 'Failed to load applications');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [filterStatus, filterJobId]);

  const handleStatusChange = async (applicationId: number, newStatus: string) => {
    try {
      const response = await httpClient.patch(`/dashboard-analytics/applications/${applicationId}/status`, {
        status: newStatus
      });
      if (response.success) {
        setApplications(prev =>
          prev.map(app =>
            app.id === applicationId
              ? { ...app, status: newStatus as JobApplication['status'] }
              : app
          )
        );
      } else {
        setError(response.error?.message || 'Failed to update status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleDownloadResume = (application: JobApplication) => {
    if (application.resume_path) {
      const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:5000/api';
      window.open(`${apiBase}/dashboard-analytics/applications/${application.id}/resume`, '_blank');
    }
  };

  const handleViewDetails = (application: JobApplication) => {
    setSelectedApplication(application);
    setDetailDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/hr/jobs')}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Job Applications
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and manage candidate applications
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Filters */}
          <Card sx={{ mb: 3, backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Job ID"
                  value={filterJobId}
                  onChange={(e) => setFilterJobId(e.target.value)}
                  placeholder="Filter by job ID"
                  sx={{ minWidth: 120 }}
                />
                <Button variant="outlined" onClick={() => { setFilterStatus(''); setFilterJobId(''); }}>
                  Clear Filters
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Applications Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : applications.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 8, backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper }}>
              <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No applications found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Applications will appear here when candidates apply for jobs
              </Typography>
            </Card>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Applicant</TableCell>
                    <TableCell>Job Position</TableCell>
                    <TableCell>Applied Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Resume</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applications.map((application) => (
                    <TableRow key={application.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {application.applicant_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {application.applicant_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {application.job_title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(application.applied_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={application.status}
                            onChange={(e) => handleStatusChange(application.id, e.target.value)}
                          >
                            {statusOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                <Chip
                                  label={opt.label}
                                  size="small"
                                  color={statusColors[opt.value]}
                                  sx={{ minWidth: 80 }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {application.resume_filename ? (
                          <Tooltip title="Download Resume">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadResume(application)}
                            >
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No resume
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(application)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Application Detail Dialog */}
          <Dialog
            open={detailDialogOpen}
            onClose={() => setDetailDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Application Details</DialogTitle>
            <DialogContent>
              {selectedApplication && (
                <Box sx={{ pt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {selectedApplication.job_title}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Name</Typography>
                        <Typography variant="body1">{selectedApplication.applicant_name}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Email</Typography>
                        <Typography variant="body1">{selectedApplication.applicant_email}</Typography>
                      </Box>
                    </Box>

                    {selectedApplication.applicant_phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Phone</Typography>
                          <Typography variant="body1">{selectedApplication.applicant_phone}</Typography>
                        </Box>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedApplication.status}
                          color={statusColors[selectedApplication.status]}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">Applied On</Typography>
                      <Typography variant="body1">{formatDate(selectedApplication.applied_at)}</Typography>
                    </Box>

                    {selectedApplication.cover_letter && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Cover Letter</Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                            p: 2,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {selectedApplication.cover_letter}
                        </Typography>
                      </Box>
                    )}

                    {selectedApplication.resume_filename && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Resume</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => handleDownloadResume(selectedApplication)}
                          >
                            {selectedApplication.resume_filename}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Layout>
  );
};

export default JobApplicationsPage;
